import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@auctisync/ui';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { SignOutButton } from '../../components/SignOutButton';
import { readClaims, useSession } from '../../hooks/useSession';
import { fetchWithAuth } from '../../lib/accessToken';

/**
 * Sales analytics — Feature 3 v1 dashboard. Cross-tenant operational
 * view, super_admin only. Hits IE Worker at `/v1/dashboard/sales`.
 *
 * No PII. Reads `public.sales_history` filtered by
 * `exclude_from_platform_pool = false` (Phase-2 opt-out flag from
 * Module #11). Section 8 layer 2 explicit cross-tenant exception.
 */

type WindowKey = '7d' | '30d' | '90d' | '365d';

const WINDOWS: readonly { key: WindowKey; label: string }[] = [
  { key: '7d', label: 'Last 7d' },
  { key: '30d', label: 'Last 30d' },
  { key: '90d', label: 'Last 90d' },
  { key: '365d', label: 'Last 365d' },
];

interface SalesKpis {
  totalLots: number;
  soldLots: number;
  sellThroughPct: number | null;
  totalSoldCents: number;
  avgFinalCents: number | null;
  medianFinalCents: number | null;
  participatingTenants: number;
}
interface TimeSeriesPoint {
  bucket: string;
  totalLots: number;
  soldLots: number;
  totalSoldCents: number;
}
interface CategorySummary {
  categoryId: string | null;
  categoryName: string | null;
  totalLots: number;
  soldLots: number;
  sellThroughPct: number | null;
  totalSoldCents: number;
  medianFinalCents: number | null;
}
interface PriceDistribution {
  p10Cents: number | null;
  p50Cents: number | null;
  p90Cents: number | null;
  p99Cents: number | null;
  sampleCount: number;
}
interface EngagementSummary {
  avgBidCount: number | null;
  medianBidCount: number | null;
  avgTimeToFirstBidSec: number | null;
  zeroBidSoldLots: number;
  hotSoldLots: number;
}
interface SalesAnalyticsSnapshot {
  windowDays: number;
  builtAtMs: number;
  windowStart: string;
  windowEnd: string;
  kpis: SalesKpis;
  daily: TimeSeriesPoint[];
  topCategories: CategorySummary[];
  priceDistribution: PriceDistribution;
  engagement: EngagementSummary;
}

interface ApiError {
  error: string;
  detail: string;
}

type FetchState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; data: SalesAnalyticsSnapshot }
  | { kind: 'error'; status: number; message: string };

function ieBaseUrl(): string | null {
  const url = import.meta.env.VITE_INTELLIGENCE_BASE_URL as string | undefined;
  return url && url.length > 0 ? url.replace(/\/$/, '') : null;
}

export function SuperSalesAnalytics() {
  const { session, userId } = useSession();
  const { appRole } = readClaims(session);
  const [windowKey, setWindowKey] = useState<WindowKey>('30d');
  const [state, setState] = useState<FetchState>({ kind: 'idle' });
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const base = ieBaseUrl();
    if (!base) {
      setState({
        kind: 'error',
        status: 0,
        message: 'VITE_INTELLIGENCE_BASE_URL is not configured.',
      });
      return;
    }
    if (userId === null) {
      setState({ kind: 'error', status: 0, message: 'No active session.' });
      return;
    }
    setState({ kind: 'loading' });
    const url = `${base}/v1/dashboard/sales?window=${windowKey}&top=15`;
    fetchWithAuth(url)
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as ApiError | null;
          setState({
            kind: 'error',
            status: res.status,
            message: body?.detail ?? `HTTP ${res.status}`,
          });
          return;
        }
        const data = (await res.json()) as SalesAnalyticsSnapshot;
        setState({ kind: 'ok', data });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'unknown error';
        setState({ kind: 'error', status: 0, message });
      });
    return () => {
      cancelled = true;
    };
  }, [windowKey, refreshNonce, userId]);

  return (
    <main style={{ padding: '2rem', maxWidth: '76rem', margin: '0 auto' }}>
      <Header session={session} appRole={appRole} />
      <Controls
        windowKey={windowKey}
        onWindow={setWindowKey}
        loading={state.kind === 'loading'}
        onRefresh={() => setRefreshNonce((n) => n + 1)}
      />

      {state.kind === 'idle' || state.kind === 'loading' ? (
        <Card>
          <CardContent>
            <p style={{ color: 'var(--text-muted)' }}>
              {state.kind === 'idle' ? 'Initializing…' : 'Loading sales analytics…'}
            </p>
          </CardContent>
        </Card>
      ) : state.kind === 'error' ? (
        <Card>
          <CardHeader>
            <CardTitle>Analytics unavailable</CardTitle>
            <CardDescription>HTTP {state.status || 'no-network'}</CardDescription>
          </CardHeader>
          <CardContent>
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                color: 'var(--semantic-danger)',
                fontFamily: 'var(--font-mono, ui-monospace)',
                fontSize: '0.85rem',
                margin: 0,
              }}
            >
              {state.message}
            </pre>
          </CardContent>
        </Card>
      ) : (
        <SnapshotView snapshot={state.data} />
      )}
    </main>
  );
}

function Header({
  session,
  appRole,
}: {
  session: ReturnType<typeof useSession>['session'];
  appRole: string | null;
}) {
  return (
    <div
      style={{
        marginBottom: '1.5rem',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '1rem',
      }}
    >
      <div>
        <h1
          style={{
            fontFamily: 'var(--font-heading-active)',
            fontSize: '1.875rem',
            fontWeight: 700,
            color: 'var(--text)',
            marginBottom: '0.25rem',
          }}
        >
          Sales analytics
        </h1>
        <p style={{ color: 'var(--text-muted)' }}>
          Signed in as {session?.user.email ?? 'unknown'} ·{' '}
          <Badge variant="outline">{appRole ?? 'no role'}</Badge> ·{' '}
          <Link to="/super/dashboard" style={{ color: 'var(--brand-primary)' }}>
            ← Super dashboard
          </Link>
        </p>
      </div>
      <SignOutButton redirectTo="/super" />
    </div>
  );
}

function Controls({
  windowKey,
  onWindow,
  loading,
  onRefresh,
}: {
  windowKey: WindowKey;
  onWindow: (k: WindowKey) => void;
  loading: boolean;
  onRefresh: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: '0.5rem',
        alignItems: 'center',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
      }}
    >
      {WINDOWS.map((w) => (
        <button
          key={w.key}
          onClick={() => onWindow(w.key)}
          type="button"
          style={{
            padding: '0.4rem 0.9rem',
            borderRadius: '0.4rem',
            border: '1px solid var(--border)',
            background: windowKey === w.key ? 'var(--brand-primary)' : 'var(--surface-elev)',
            color: windowKey === w.key ? 'var(--brand-primary-fg)' : 'var(--text)',
            fontWeight: 600,
            fontSize: '0.85rem',
            cursor: 'pointer',
          }}
        >
          {w.label}
        </button>
      ))}
      <button
        onClick={onRefresh}
        type="button"
        disabled={loading}
        style={{
          padding: '0.4rem 0.9rem',
          borderRadius: '0.4rem',
          border: '1px solid var(--border)',
          background: 'var(--surface-elev)',
          color: 'var(--text)',
          fontWeight: 600,
          fontSize: '0.85rem',
          cursor: loading ? 'wait' : 'pointer',
          marginLeft: 'auto',
        }}
      >
        {loading ? 'Loading…' : 'Refresh'}
      </button>
    </div>
  );
}

function SnapshotView({ snapshot }: { snapshot: SalesAnalyticsSnapshot }) {
  const ws = snapshot.windowStart.slice(0, 10);
  const we = snapshot.windowEnd.slice(0, 10);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(11rem, 1fr))',
          gap: '0.75rem',
        }}
      >
        <KPI label="Sell-through" value={fmtPct(snapshot.kpis.sellThroughPct)} accent />
        <KPI
          label="Sold lots"
          value={snapshot.kpis.soldLots.toLocaleString()}
          sub={`of ${snapshot.kpis.totalLots.toLocaleString()} terminal`}
        />
        <KPI label="Total sold" value={fmtUsd(snapshot.kpis.totalSoldCents)} />
        <KPI label="Median sale" value={fmtUsd(snapshot.kpis.medianFinalCents)} />
        <KPI label="Avg sale" value={fmtUsd(snapshot.kpis.avgFinalCents)} />
        <KPI
          label="Tenants"
          value={snapshot.kpis.participatingTenants.toLocaleString()}
          sub="participating"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Pricing distribution</CardTitle>
          <CardDescription>
            Across {snapshot.priceDistribution.sampleCount.toLocaleString()} sold lots between {ws}{' '}
            and {we}.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(8rem, 1fr))',
              gap: '0.75rem',
            }}
          >
            <KPI label="p10" value={fmtUsd(snapshot.priceDistribution.p10Cents)} />
            <KPI label="p50 (median)" value={fmtUsd(snapshot.priceDistribution.p50Cents)} accent />
            <KPI label="p90" value={fmtUsd(snapshot.priceDistribution.p90Cents)} />
            <KPI label="p99" value={fmtUsd(snapshot.priceDistribution.p99Cents)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Top categories</CardTitle>
          <CardDescription>By terminal lots in window. Sorted high to low.</CardDescription>
        </CardHeader>
        <CardContent>
          {snapshot.topCategories.length === 0 ? (
            <p style={{ color: 'var(--text-muted)' }}>No categorized sales in window.</p>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Category</Th>
                  <Th align="right">Lots</Th>
                  <Th align="right">Sold</Th>
                  <Th align="right">Sell-through</Th>
                  <Th align="right">Median</Th>
                  <Th align="right">Total sold</Th>
                </tr>
              </thead>
              <tbody>
                {snapshot.topCategories.map((c) => (
                  <tr key={c.categoryId ?? '__uncat__'}>
                    <Td>
                      {c.categoryName ?? (
                        <em style={{ color: 'var(--text-muted)' }}>uncategorized</em>
                      )}
                    </Td>
                    <Td align="right">{c.totalLots.toLocaleString()}</Td>
                    <Td align="right">{c.soldLots.toLocaleString()}</Td>
                    <Td align="right">{fmtPct(c.sellThroughPct)}</Td>
                    <Td align="right">{fmtUsd(c.medianFinalCents)}</Td>
                    <Td align="right">{fmtUsd(c.totalSoldCents)}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Engagement</CardTitle>
          <CardDescription>
            Across {snapshot.priceDistribution.sampleCount.toLocaleString()} sold lots.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(11rem, 1fr))',
              gap: '0.75rem',
            }}
          >
            <KPI
              label="Avg bid count"
              value={
                snapshot.engagement.avgBidCount === null
                  ? '—'
                  : snapshot.engagement.avgBidCount.toFixed(1)
              }
            />
            <KPI
              label="Median bid count"
              value={
                snapshot.engagement.medianBidCount === null
                  ? '—'
                  : snapshot.engagement.medianBidCount.toLocaleString()
              }
            />
            <KPI
              label="Avg time to 1st bid"
              value={fmtSec(snapshot.engagement.avgTimeToFirstBidSec)}
            />
            <KPI
              label="Hot lots (≥10 bids)"
              value={snapshot.engagement.hotSoldLots.toLocaleString()}
              accent
            />
            <KPI
              label="Zero-bid sold"
              value={snapshot.engagement.zeroBidSoldLots.toLocaleString()}
              sub="should be 0"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Daily activity</CardTitle>
          <CardDescription>
            Last {snapshot.windowDays} days · {snapshot.daily.length} buckets with activity.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {snapshot.daily.length === 0 ? (
            <p style={{ color: 'var(--text-muted)' }}>No sales activity in window.</p>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Day (UTC)</Th>
                  <Th align="right">Lots</Th>
                  <Th align="right">Sold</Th>
                  <Th align="right">Total sold</Th>
                </tr>
              </thead>
              <tbody>
                {snapshot.daily
                  .slice(-30)
                  .reverse()
                  .map((d) => (
                    <tr key={d.bucket}>
                      <Td>{d.bucket}</Td>
                      <Td align="right">{d.totalLots.toLocaleString()}</Td>
                      <Td align="right">{d.soldLots.toLocaleString()}</Td>
                      <Td align="right">{fmtUsd(d.totalSoldCents)}</Td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KPI({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: ReactNode;
  sub?: string;
  accent?: boolean;
}) {
  return (
    <div
      style={{
        background: 'var(--surface-elev)',
        border: '1px solid var(--border)',
        borderRadius: '0.5rem',
        padding: '0.85rem 1rem',
      }}
    >
      <div
        style={{
          fontSize: '0.7rem',
          color: 'var(--text-muted)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          marginBottom: '0.3rem',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: '1.4rem',
          fontWeight: 700,
          color: accent ? 'var(--brand-primary)' : 'var(--text)',
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      {sub !== undefined && (
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.3rem' }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function Table({ children }: { children: ReactNode }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.9rem',
        }}
      >
        {children}
      </table>
    </div>
  );
}

function Th({ children, align }: { children: ReactNode; align?: 'right' | 'left' }) {
  return (
    <th
      style={{
        textAlign: align ?? 'left',
        padding: '0.5rem 0.6rem',
        borderBottom: '1px solid var(--border)',
        color: 'var(--text-muted)',
        fontWeight: 600,
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, align }: { children: ReactNode; align?: 'right' | 'left' }) {
  return (
    <td
      style={{
        textAlign: align ?? 'left',
        padding: '0.45rem 0.6rem',
        borderBottom: '1px solid var(--border-subtle, var(--border))',
        color: 'var(--text)',
      }}
    >
      {children}
    </td>
  );
}

function fmtUsd(cents: number | null): string {
  if (cents === null) return '—';
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtPct(value: number | null): string {
  if (value === null) return '—';
  return `${value.toFixed(value < 10 ? 2 : 1)}%`;
}

function fmtSec(value: number | null): string {
  if (value === null) return '—';
  if (value < 60) return `${value.toFixed(0)}s`;
  if (value < 3600) return `${(value / 60).toFixed(1)}m`;
  return `${(value / 3600).toFixed(1)}h`;
}
